/**
 * Groups the backend's flat permission catalogue into something readable.
 *
 * The backend stays the source of truth for *which* permissions exist - this file
 * only decides the order they are shown in and which ones get a warning. A
 * permission the backend reports but nothing here claims still shows up, under
 * "Sonstiges", so a new backend permission is never silently unassignable.
 */

export const DANGEROUS_PERMISSIONS = [
    'all_permissions',
    'manage.admins',
    'settings.write',
    'console.write',
] as const;

export const isDangerousPermission = (perm: string) => {
    return (DANGEROUS_PERMISSIONS as readonly string[]).includes(perm);
};

type CategorySpec = {
    label: string;
    permissions: string[];
};

const categorySpecs: CategorySpec[] = [
    {
        label: 'Administration',
        permissions: [
            'all_permissions',
            'manage.admins',
            'settings.view',
            'settings.write',
            'txadmin.log.view',
        ],
    },
    {
        label: 'Server',
        permissions: [
            'console.view',
            'console.write',
            'control.server',
            'announcement',
            'commands.resources',
            'server.cfg.editor',
            'server.log.view',
        ],
    },
    {
        label: 'Spieler',
        permissions: [
            'players.direct_message',
            'players.whitelist',
            'players.warn',
            'players.kick',
            'players.ban',
            'players.remove_ids',
            'players.freeze',
            'players.heal',
            'players.playermode',
            'players.spectate',
            'players.teleport',
            'players.troll',
        ],
    },
    {
        label: 'Ingame-Menü',
        permissions: [
            'menu.vehicle',
            'menu.clear_area',
            'menu.viewids',
        ],
    },
];

export type PermissionItem = {
    id: string;
    label: string;
    dangerous: boolean;
};

export type PermissionCategory = {
    label: string;
    permissions: PermissionItem[];
};

/**
 * Turns the backend's `{id: label}` map into ordered categories.
 * @param registered the `permissions` field of the admin manager list response
 */
export const groupPermissions = (registered: Record<string, string>): PermissionCategory[] => {
    const claimed = new Set<string>();
    const categories: PermissionCategory[] = [];

    for (const spec of categorySpecs) {
        const permissions: PermissionItem[] = [];
        for (const id of spec.permissions) {
            if (!(id in registered)) continue;
            claimed.add(id);
            permissions.push({ id, label: registered[id], dangerous: isDangerousPermission(id) });
        }
        if (permissions.length) categories.push({ label: spec.label, permissions });
    }

    const leftovers = Object.keys(registered)
        .filter((id) => !claimed.has(id))
        .sort()
        .map((id) => ({ id, label: registered[id], dangerous: isDangerousPermission(id) }));
    if (leftovers.length) categories.push({ label: 'Sonstiges', permissions: leftovers });

    return categories;
};
